import {
  Component,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import {
  GoogleMap,
  MapInfoWindow,
  MapPolygon,
  MapPolyline,
} from '@angular/google-maps';
import {
  IAmbiente,
  ICoordenadas,
  IEstacionMeteorologica,
  IFreatimetro,
  ILanzaHumedad,
  ILuminaria,
  IPivot,
  IPluviometro,
  IPolyline,
  ISensorHumedadSuelo,
  IUbicacion,
} from 'modelos/src';
import { DialogService } from '../dialog/dialog.service';
import { HelperService } from '../helper.service';
import { LuminariasService } from 'src/app/modulos/aplicaciones/luminarias/service';
import { MatDialog } from '@angular/material/dialog';
import { DetallesModalComponent } from 'src/app/modulos/aplicaciones/luminarias/luminarias-map/detalles-modal/detalles-modal.component';

interface PolygonOptionsWithId extends google.maps.PolygonOptions {
  _id?: string;
}

interface PolylineOptionssWithId extends google.maps.PolylineOptions {
  _id?: string;
}

export interface IUbicacionConId extends IUbicacion {
  _id?: string;
  colorPrediccion?: string; // Color de la predicción del ambiente
}

@Component({
  selector: 'app-polygon-draw',
  templateUrl: './polygon-draw.component.html',
  styleUrls: ['./polygon-draw.component.scss'],
})
export class PolygonDrawComponent implements OnInit, OnChanges {
  public dibujando = true;
  public circuloDibujado?: { centro: ICoordenadas; radio: number };

  @Input() mapTypeId: google.maps.MapTypeId = google.maps.MapTypeId.SATELLITE;
  @Input() showMapTypeControl: boolean = true;
  @Input() showMaptypeControlPosition: google.maps.ControlPosition =
    google.maps.ControlPosition.TOP_RIGHT;

  @Input() showZoomMapControl: boolean = true;
  @Input() showZoomMapControlPosition: google.maps.ControlPosition =
    google.maps.ControlPosition.TOP_LEFT;

  // Poligono a dibujar
  @Input() multiple: boolean = false;
  @Input() esPolyline: boolean = false;
  @Input() esCircle: boolean = false;
  @Input() ubicaciones?: IUbicacionConId[] = [];
  @Input() ambiente?: IAmbiente;
  @Input() selectAmbiente?: (
    id: string | undefined,
    callback?: () => void,
  ) => Promise<void>;
  @Input() lineas: IPolyline[] = [];
  @Input() editable: boolean = true;
  @Input() onPolygonClickCallback?: (ubicacionId: string) => void;
  @Input() onDispositivoClickCallback?: (dispositivo: any) => void;

  @Input() cLinea?: string;
  @Input() cRelleno?: string;
  @Input() opacidad?: number;

  @Input() cLineaSelectedUbicacion?: string;
  @Input() cRellenoSelectedUbicacion?: string;
  @Input() opacidadSelectedUbicacion?: number;

  // Mapa
  @Input() height: string = '50vh';
  @Input() zoom: number = 15;
  @ViewChild(GoogleMap, { static: false }) map: GoogleMap | undefined;
  @Input() public center?: google.maps.LatLngLiteral;
  public mapOptions?: google.maps.MapOptions;

  // Sensores
  @Input() ubicacionLanzasHumedad: ILanzaHumedad[] = [];
  @Input() ubicacionSensoresHumedadSuelo: ISensorHumedadSuelo[] = [];
  @Input() ubicacionPluviometros: IPluviometro[] = [];
  @Input() ubicacionEstacion: IEstacionMeteorologica[] = [];
  @Input() ubicacionFreatimetro: IFreatimetro[] = [];
  @Input() ubicacionLuminaria: ILuminaria[] = [];
  @Input() pivots: IPivot[] = [];
  @Input() ubicacionPresionAgua: any[] = []; // Para dispositivos de presión de agua

  // luminarias filtrada
  public luminariasFiltradas: ILuminaria[] = [];

  // Otros Poligonos Fijos de referencia
  @Input() otrasUbicaciones: IUbicacion[] = [];
  @ViewChildren(MapPolygon) polygonRef?: QueryList<MapPolygon>;
  @ViewChildren(MapPolyline) polylineRef?: QueryList<MapPolyline>;
  public polygonOptions: PolygonOptionsWithId[] = [];
  public polylineOptions: PolylineOptionssWithId[] = [];
  public circleOptions: google.maps.CircleOptions[] = [];
  public selectedPoly?: number;

  private markers: google.maps.Marker[] = [];

  public agregandoPuntoLlegada = false;
  public pivotSeleccionadoId?: string;

  // Info Window
  @ViewChild(MapInfoWindow) infoWindow?: MapInfoWindow;

  // Dibujo de poligono
  public drawingManager = new google.maps.drawing.DrawingManager({
    polygonOptions: {
      editable: this.editable === false ? false : true,
      draggable: false,
      clickable: true,
      zIndex: 100,
      strokeColor: this.cLinea,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: this.cRelleno,
      fillOpacity: 0.35,
    },
    polylineOptions: {
      editable: this.editable === false ? false : true,
      draggable: false,
      clickable: true,
      zIndex: 100,
      strokeColor: this.cLinea,
      strokeOpacity: 0.8,
      strokeWeight: 5,
    },
    circleOptions: {
      editable: this.editable === false ? false : true,
      strokeColor: this.cLinea,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: this.cRelleno,
      fillOpacity: 0.35,
      clickable: true,
    },
    drawingMode:
      this.esPolyline && this.editable === true
        ? google.maps.drawing.OverlayType.POLYLINE
        : google.maps.drawing.OverlayType.POLYGON,
    drawingControl: true,
    drawingControlOptions: {
      position: google.maps.ControlPosition.TOP_CENTER,
      drawingModes: [
        google.maps.drawing.OverlayType.POLYGON,
        google.maps.drawing.OverlayType.POLYLINE,
        google.maps.drawing.OverlayType.CIRCLE,
      ],
    },
  });

  get polygons(): ICoordenadas[][] {
    const response: ICoordenadas[][] = [];
    if (this.polygonRef) {
      const polygons = this.polygonRef.toArray();
      for (const polygon of polygons) {
        response.push(
          polygon
            ?.getPath()
            ?.getArray()
            .map((position) => {
              return { lat: position.lat(), lng: position.lng() };
            }),
        );
      }
    }
    return response;
  }

  get polylines(): ICoordenadas[][] {
    const response: ICoordenadas[][] = [];
    if (this.polylineRef) {
      const polygons = this.polylineRef.toArray();
      for (const polygon of polygons) {
        response.push(
          polygon
            ?.getPath()
            ?.getArray()
            .map((position) => {
              return { lat: position.lat(), lng: position.lng() };
            }),
        );
      }
    }
    return response;
  }

  get getUbicaciones(): IUbicacion[] {
    const ubicaciones: IUbicacion[] = [];
    if (this.esCircle && this.circuloDibujado) {
      // Calcular el área del círculo usando la fórmula matemática
      const radio = this.circuloDibujado.radio;
      const areaM2 = Math.PI * Math.pow(radio, 2);
      const areaHectareas = +(areaM2 / 10000).toFixed(2);

      return [
        {
          poligono: [
            {
              lat: 0,
              lng: 0,
            },
          ],
          circulo: this.circuloDibujado,
          centro: this.circuloDibujado.centro,
          nombre: '',
          superficie: areaHectareas,
        },
      ];
    }

    for (const polygon of this.polygons) {
      ubicaciones.push({
        poligono: polygon,
        nombre: '',
        centro: this.getCentro(polygon),
        superficie: this.calcularArea(polygon),
      });
    }
    return ubicaciones;
  }

  get getLineas(): IPolyline[] {
    const lineas: IPolyline[] = [];
    console.log(`poly en get`, this.polylines);

    for (const polyline of this.polylines) {
      lineas.push(polyline);
    }
    return lineas;
    // const lineas: IPolyline[] = [];
    // for (const polyline of this.polylineOptions) {
    //   lineas.push(polyline.path as IPolyline);
    // }
    // return lineas;
  }

  constructor(
    private helper: HelperService,
    private dalogService: DialogService,
    private luminariasService: LuminariasService,
    private dialog: MatDialog,
  ) {}

  private getCentro(coordenadas?: ICoordenadas[]): ICoordenadas {
    if (coordenadas?.length) {
      const newPolygon: ICoordenadas[] = JSON.parse(
        JSON.stringify(coordenadas),
      );
      const longitudes = newPolygon.map((i) => i.lat);
      const latitudes = newPolygon.map((i) => i.lng);
      latitudes.sort((a, b) => a - b);
      longitudes.sort((a, b) => a - b);
      const lowX = latitudes[0];
      const highX = latitudes[latitudes.length - 1];
      const lowy = longitudes[0];
      const highy = longitudes[latitudes.length - 1];
      const centerX = lowX + (highX - lowX) / 2;
      const centerY = lowy + (highy - lowy) / 2;
      const center: ICoordenadas = {
        lng: centerX,
        lat: centerY,
      };
      return center;
    }
    return { lng: 0, lat: 0 };
  }
  private calcularAreas(poligonos: ICoordenadas[][]): number {
    if (poligonos?.length) {
      let area = 0;
      for (const poly of poligonos) {
        area += this.calcularArea(poly);
      }
      return +area.toFixed(2);
    }
    return 0;
  }
  private calcularArea(poligono: ICoordenadas[]): number {
    if (poligono?.length > 2) {
      const poly = new google.maps.Polygon({ paths: poligono });
      const area = google.maps.geometry.spherical.computeArea(poly.getPath());
      return +(area / 10000).toFixed(2);
    }
    return 0;
  }

  async handlePolygonClick(
    event: google.maps.PolyMouseEvent,
    polygon: any,
  ): Promise<void> {
    // Si hay un callback personalizado, usarlo y salir
    if (this.onPolygonClickCallback && polygon._id) {
      this.onPolygonClickCallback(polygon._id);
      return;
    }

    // Comportamiento original para ambientes (sin cambios)
    if (polygon._id === this.ambiente?._id) {
      await this.mostrarInfoWindow(event, polygon);
    } else if (this.selectAmbiente && polygon?._id !== this.ambiente?._id) {
      await this.selectAmbiente(polygon._id, () =>
        this.mostrarInfoWindow(event, polygon),
      );
    }
  }

  async handlePolylineClick(
    event: google.maps.PolyMouseEvent,
    polyline: any,
  ): Promise<void> {
    if (polyline._id === this.ambiente?._id) {
      await this.mostrarInfoWindow(event, polyline);
    } else if (this.selectAmbiente && polyline?._id !== this.ambiente?._id) {
      await this.selectAmbiente(polyline._id, () =>
        this.mostrarInfoWindow(event, polyline),
      );
    }
  }

  public async handleCircleClick(
    event: google.maps.MapMouseEvent,
    circle: google.maps.CircleOptions,
  ): Promise<void> {
    // Encontrar el ambiente asociado a este círculo
    const ubicacion = this.ubicaciones?.find(
      (u) =>
        u.circulo &&
        u.circulo.centro.lat === circle.center?.lat &&
        u.circulo.centro.lng === circle.center?.lng &&
        u.circulo.radio === circle.radius,
    );
    // Si hay selectAmbiente y el ambiente no está seleccionado, seleccionarlo
    if (
      ubicacion &&
      this.selectAmbiente &&
      ubicacion._id !== this.ambiente?._id
    ) {
      await this.selectAmbiente(ubicacion._id, () =>
        this.mostrarInfoWindow(event, { _id: ubicacion._id }),
      );
    } else if (ubicacion && ubicacion._id === this.ambiente?._id) {
      await this.mostrarInfoWindow(event, { _id: ubicacion._id });
    }
  }

  public async mostrarInfoWindow(
    event: google.maps.PolyMouseEvent,
    poli?: any,
  ) {
    if (this.infoWindow) {
      this.selectedPoly = poli._id;
      this.infoWindow.position = event.latLng!;
      this.infoWindow.options = {
        maxWidth: 600,
        minWidth: 400,
      };
      this.infoWindow!.open();
    }
  }

  activarAgregarPuntoLlegada() {
    console.log('🔴 Activando modo agregar punto llegada');
    console.log('Pivot seleccionado:', this.pivotSeleccionadoId);
    console.log('Ambiente:', this.ambiente);

    // if (!this.pivotSeleccionadoId) {
    //   console.error('❌ No hay pivot seleccionado');
    //   return;
    // }

    this.agregandoPuntoLlegada = true;
    console.log('✅ Modo activado, haz clic en el mapa');
  }

  puntoEnCircunferencia(
    centro: { lat: number; lng: number },
    radio: number,
    angulo: number,
  ) {
    const R = 6378137;
    const anguloRad = (angulo * Math.PI) / 180;
    const lat1 = (centro.lat * Math.PI) / 180;
    const lng1 = (centro.lng * Math.PI) / 180;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(radio / R) +
        Math.cos(lat1) * Math.sin(radio / R) * Math.cos(anguloRad),
    );
    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(anguloRad) * Math.sin(radio / R) * Math.cos(lat1),
        Math.cos(radio / R) - Math.sin(lat1) * Math.sin(lat2),
      );
    return {
      lat: (lat2 * 180) / Math.PI,
      lng: (lng2 * 180) / Math.PI,
    };
  }

  onMapClick(event: google.maps.MapMouseEvent) {
    if (
      !this.agregandoPuntoLlegada ||
      (!this.circuloDibujado && !this.ambiente?.ubicacion?.circulo)
    ) {
      console.error('❌ No hay círculo disponible');
      return;
    }

    // ✅ Usar el círculo del ambiente si no hay circuloDibujado
    const circulo = this.circuloDibujado || this.ambiente?.ubicacion?.circulo;
    if (!circulo) {
      console.error('No hay círculo disponible');
      return;
    }

    const centro = circulo.centro;
    const radio = circulo.radio;

    // Calcular ángulo entre centro y click
    const dx = event.latLng!.lng() - centro.lng;
    const dy = event.latLng!.lat() - centro.lat;
    const angulo = (Math.atan2(dy, dx) * 180) / Math.PI;

    const punto = this.puntoEnCircunferencia(centro, radio, angulo);

    // Buscar el pivot seleccionado y agregar el punto
    const pivot = this.pivots.find((p) => p._id === this.pivotSeleccionadoId);
    console.log('Pivot encontrado:', pivot);
    if (pivot) {
      if (!pivot.puntosLlegadaGps) pivot.puntosLlegadaGps = [];
      const numeroPunto = String(pivot.puntosLlegadaGps.length + 1);

      pivot.puntosLlegadaGps.push({
        ...punto,
        numero: numeroPunto,
        desc: `Punto de llegada ${numeroPunto}`,
        fecha: new Date().toISOString(),
      });

      console.log(
        'Punto agregado:',
        pivot.puntosLlegadaGps[pivot.puntosLlegadaGps.length - 1],
      );
    }

    this.agregandoPuntoLlegada = false;
  }

  public hayElementosParaEliminar(): boolean {
    if (this.esCircle) {
      return !!(this.circuloDibujado || this.circleOptions.length > 0);
    } else if (this.esPolyline) {
      return this.polylineOptions.length > 0;
    } else {
      return this.polygonOptions.length > 0;
    }
  }

  public async eliminar() {
    const confirm = await this.dalogService.confirm(
      'Por favor confirme la acción',
      this.esPolyline
        ? '¿Eliminar la polyline seleccionada?'
        : this.esCircle
          ? '¿Eliminar el círculo?'
          : '¿Eliminar el polígono seleccionado?',
    );

    if (confirm) {
      if (this.esPolyline) {
        // Para polylines: eliminar el seleccionado o el último
        const indexToDelete =
          this.selectedPoly !== undefined
            ? this.selectedPoly
            : this.polylineOptions.length - 1;
        this.polylineOptions.splice(indexToDelete, 1);
        this.helper.notifSuccess('Polyline eliminada');
      } else if (this.esCircle && this.circuloDibujado) {
        // Para círculos nuevos
        this.circuloDibujado = undefined;
        this.helper.notifSuccess('Círculo eliminado');
      } else if (this.esCircle && this.circleOptions.length > 0) {
        // Para círculos existentes
        const indexToDelete =
          this.selectedPoly !== undefined
            ? this.selectedPoly
            : this.circleOptions.length - 1;
        this.circleOptions.splice(indexToDelete, 1);
        this.helper.notifSuccess('Círculo eliminado');
      } else if (!this.esCircle && !this.esPolyline) {
        // Para polígonos: eliminar el seleccionado o el último
        const indexToDelete =
          this.selectedPoly !== undefined
            ? this.selectedPoly
            : this.polygonOptions.length - 1;
        this.polygonOptions.splice(indexToDelete, 1);
        this.helper.notifSuccess('Polígono eliminado');
      }

      // Limpiar selección
      this.selectedPoly = undefined;
    }
    this.infoWindow?.close();
  }

  public dibujar() {
    if (this.map?.googleMap) {
      this.initDrawingManager();
      this.drawingManager.setMap(this.map?.googleMap || null);
      this.dibujando = true;
    } else {
      setTimeout(() => this.dibujar(), 50);
    }
  }

  public listenDrawEnd() {
    google.maps.event.addListener(
      this.drawingManager,
      'overlaycomplete',
      async (event: any) => {
        // Círculo
        if (
          !this.esPolyline &&
          this.esCircle &&
          event.type === google.maps.drawing.OverlayType.CIRCLE
        ) {
          const circle = event.overlay;
          const centro = {
            lat: circle.getCenter().lat(),
            lng: circle.getCenter().lng(),
          };
          const radio = circle.getRadius();
          this.circleOptions.push({
            center: centro,
            radius: radio,
            draggable: false,
            editable: this.editable === false ? false : true,
            clickable: true,
            zIndex: 100,
            strokeColor: this.cLinea,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: this.cRelleno,
            fillOpacity: 0.35,
          });
          // Guardar la geometría para getUbicaciones
          this.circuloDibujado = { centro, radio };
          this.dibujando = false;
          event.overlay.setMap(null);
          this.drawingManager.setMap(null);
        }
        // Polygon drawn
        if (
          !this.esPolyline &&
          !this.esCircle &&
          event.type === google.maps.drawing.OverlayType.POLYGON
        ) {
          const coordenadas: ICoordenadas[] = [];
          event.overlay
            .getPath()
            .getArray()
            .forEach((position: any) => {
              const pos: ICoordenadas = {
                lat: position.lat(),
                lng: position.lng(),
              };
              coordenadas.push(pos);
            });
          this.polygonOptions.push({
            draggable: false,
            editable: this.editable === false ? false : true,
            clickable: true,
            paths: coordenadas,
            zIndex: 100,
            strokeColor: this.cLinea,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: this.cRelleno,
            fillOpacity: 0.35,
          });
          this.selectedPoly = this.polygonOptions.length - 1;
          this.dibujando = false;
          event.overlay.setMap(null);
          this.drawingManager.setMap(null);
        }
        // Polyline drawn
        if (
          this.esPolyline &&
          event.type === google.maps.drawing.OverlayType.POLYLINE
        ) {
          const coordenadas: ICoordenadas[] = [];
          event.overlay
            .getPath()
            .getArray()
            .forEach((position: any) => {
              const pos: ICoordenadas = {
                lat: position.lat(),
                lng: position.lng(),
              };
              coordenadas.push(pos);
            });

          this.polylineOptions.push({
            draggable: false,
            editable: this.editable === false ? false : true,
            clickable: true,
            path: coordenadas,
            zIndex: 100,
            strokeColor: this.cLinea,
            strokeOpacity: 0.8,
            strokeWeight: 5,
          });
          this.selectedPoly = this.polylineOptions.length - 1;
          this.dibujando = false;
          event.overlay.setMap(null);
          this.drawingManager.setMap(null);
        }
      },
    );
  }

  public cancelarDibujo() {
    this.dibujando = false;
    this.drawingManager.setMap(null);
  }

  private async setMapOptions() {
    this.mapOptions = {
      ...JSON.parse(JSON.stringify(HelperService.mapOptionsMarkerPicker)),
      zoom: this.zoom,

      mapTypeId: this.mapTypeId,
      mapTypeControl: this.showMapTypeControl,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle.DEFAULT,
        position: this.showMaptypeControlPosition,
      },
      zoomControl: this.showZoomMapControl,
      zoomControlOptions: {
        position: this.showZoomMapControlPosition,
      },
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }], // Oculta los puntos de interés (marcadores de Google)
        },
      ],
    };
  }

  private initDrawingManager() {
    if (this.esPolyline) {
      this.drawingManager = new google.maps.drawing.DrawingManager({
        polylineOptions: {
          editable: this.editable === false ? false : true,
          draggable: false,
          clickable: true,
          zIndex: 100,
          strokeColor: this.cLinea,
          strokeOpacity: 0.8,
          strokeWeight: 5,
        },
        drawingMode:
          this.editable === false
            ? null
            : google.maps.drawing.OverlayType.POLYLINE,
        drawingControl: this.editable === false ? false : true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [google.maps.drawing.OverlayType.POLYLINE],
        },
      });
    } else {
      const drawingMode: google.maps.drawing.OverlayType =
        this.esCircle === true
          ? google.maps.drawing.OverlayType.CIRCLE
          : google.maps.drawing.OverlayType.POLYGON;

      const drawingModes = [drawingMode];

      this.drawingManager = new google.maps.drawing.DrawingManager({
        polygonOptions: {
          editable: this.editable === false ? false : true,
          draggable: false,
          clickable: true,
          zIndex: 100,
          strokeColor: this.cLinea,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: this.cRelleno,
          fillOpacity: 0.35,
        },
        circleOptions: {
          // Opcional: podés definir opciones para el círculo
          editable: this.editable === false ? false : true,
          draggable: false,
          clickable: true,
          zIndex: 100,
          strokeColor: this.cLinea,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: this.cRelleno,
          fillOpacity: 0.35,
        },
        drawingMode: this.editable === false ? null : drawingMode,
        drawingControl: this.editable === false ? false : true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: drawingModes,
        },
      });
    }

    this.listenDrawEnd();
  }

  private processUbicaciones() {
    this.polygonOptions = [];
    this.circleOptions = [];

    if (this.ubicaciones !== undefined && this.ubicaciones.length > 0) {
      this.center = this.ubicaciones[0].centro;
      if (!this.center) {
        this.center = this.ubicaciones[0].centro || {
          lat: -34.603722,
          lng: -58.381592,
        };
      }
      for (const ubicacion of this.ubicaciones) {
        if (ubicacion.circulo) {
          // Si tiene círculo, agregarlo a circleOptions
          this.circleOptions.push({
            center: ubicacion.circulo.centro,
            radius: ubicacion.circulo.radio,
            draggable: false,
            editable: this.editable === false ? false : true,
            clickable: true,
            zIndex: 1,
            strokeColor:
              ubicacion.colorPrediccion || this.cLineaSelectedUbicacion,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor:
              ubicacion.colorPrediccion || this.cRellenoSelectedUbicacion,
            fillOpacity: this.opacidadSelectedUbicacion || 0.35,
          });
        } else {
          this.polygonOptions.push({
            draggable: false,
            editable: this.editable === false ? false : true,
            paths: ubicacion.poligono,
            strokeColor:
              ubicacion.colorPrediccion || this.cLineaSelectedUbicacion,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor:
              ubicacion.colorPrediccion || this.cRellenoSelectedUbicacion,
            fillOpacity: this.opacidadSelectedUbicacion || 0.35,
            _id: ubicacion._id,
          });
        }
      }
    }
    if (this.ambiente !== undefined && this.ambiente.ubicacion) {
      this.center = this.ambiente.ubicacion.centro;
      if (!this.center) {
        this.center = this.ambiente?.ubicacion?.centro || {
          lat: -34.603722,
          lng: -58.381592,
        };
      }
      if (!this.ambiente.ubicacion.circulo) {
        this.polygonOptions.push({
          draggable: false,
          editable: this.editable === false ? false : true,
          paths: this.ambiente.ubicacion.poligono,
          strokeColor: this.cLinea,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: this.cRelleno,
          fillOpacity: 0.35,
          _id: this.ambiente._id,
        });
      } else {
        this.circleOptions.push({
          center: this.ambiente.ubicacion.circulo.centro,
          radius: this.ambiente.ubicacion.circulo.radio,
          draggable: false,
          editable: this.editable === false ? false : true,
          clickable: true,
          zIndex: 100,
          strokeColor: this.cLinea,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: this.cRelleno,
          fillOpacity: 0.35,
        });
        this.center = this.ambiente.ubicacion.circulo.centro;
      }
    }
  }

  private async initMap() {
    await this.setMapOptions();
    await this.updateMarkers();
    if (this.esPolyline) {
      if (this.lineas?.length > 0) {
        if (!this.center) {
          this.center = this.lineas[0][0] || {
            lat: -34.603722,
            lng: -58.381592,
          };
        }
        for (const linea of this.lineas) {
          this.polylineOptions.push({
            draggable: false,
            editable: this.editable === false ? false : true,
            path: linea,
            strokeColor: this.cLinea,
            strokeOpacity: 0.8,
            strokeWeight: 5,
          });
        }
        this.dibujando = false;
      } else if (this.otrasUbicaciones.length) {
        if (!this.center) {
          this.center = this.otrasUbicaciones[0].centro;
        }
        this.dibujar();
        this.dibujando = false;
      } else {
        const ubicacion = await HelperService.getCurrentPosition();
        if (!this.center) {
          this.center = ubicacion;
        }
        this.dibujar();
        this.dibujando = false;
      }
    } else {
      if (this.ubicaciones !== undefined && this.ubicaciones?.length > 0) {
        this.processUbicaciones();
        this.dibujando = false;
      } else if (this.ambiente !== undefined && this.ambiente.ubicacion) {
        this.processUbicaciones();
        this.dibujando = false;
      } else {
        const ubicacion = await HelperService.getCurrentPosition();
        if (!this.center) {
          this.center = ubicacion;
        }
        this.dibujar();
        this.dibujando = false;
      }
    }
  }

  public mostrarCrear() {
    if (this.dibujando) return false;

    if (this.multiple) return true;

    if (this.esPolyline) {
      const polylines = this.polylineRef?.toArray();
      if (polylines && polylines.length < 1) return true;
    } else {
      const polygons = this.polygonRef?.toArray();
      if (polygons && polygons.length < 1) return true;
    }

    return false;
  }

  private openDetallesModal(luminaria: ILuminaria): void {
    this.dialog.open(DetallesModalComponent, {
      width: '80%',
      data: { luminaria: luminaria },
    });
  }

  private openDispositivoModal(dispositivo: any): void {
    if (this.onDispositivoClickCallback) {
      this.onDispositivoClickCallback(dispositivo);
    }
  }

  private async addMarker(
    location: ICoordenadas,
    title: string,
    iconUrl: string = '',
    luminaria?: ILuminaria,
    dispositivo?: any,
  ) {
    const marker = new google.maps.Marker({
      position: { lat: location.lat, lng: location.lng },
      map: this.map?.googleMap,
      title: title,
      icon: {
        url: iconUrl,
        scaledSize: new google.maps.Size(40, 40),
      },
    });

    if (luminaria) {
      marker.addListener('click', () => {
        this.openDetallesModal(luminaria);
      });
    } else if (dispositivo) {
      marker.addListener('click', () => {
        this.openDispositivoModal(dispositivo);
      });
    }
    this.markers.push(marker);
  }

  private clearMarkers() {
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
  }

  actualizarLuminariasFiltradas(): void {
    this.luminariasFiltradas =
      this.luminariasService.obtenerLuminariasFiltradas(
        this.ubicacionLuminaria,
      );
  }

  private async updateMarkers() {
    this.clearMarkers();

    // Icon URLs
    const shsIconUrl = '../../../assets/svg/map/shs-map.svg';
    const pluviometroIconUrl = '../../../assets/svg/map/pluviometro-map.svg';
    const estacionIconUrl = '../../../assets/svg/map/estacion-map.svg';
    const freatimetroIconUrl = '../../../assets/svg/map/freatimetro-map.svg';
    const presionAguaIconUrl = '../../../assets/svg/map/presion-agua.svg';

    // Luminarias
    if (this.luminariasFiltradas.length > 0) {
      this.luminariasFiltradas.forEach((sensor) => {
        if (
          (sensor?.ubicacion !== null && sensor?.ubicacion !== undefined) ||
          (sensor?.dispositivo?.config?.location !== null &&
            sensor?.dispositivo?.config?.location !== undefined)
        ) {
          this.addMarker(
            sensor?.ubicacion || sensor?.dispositivo?.config?.location,
            'Luminaria',
            this.luminariasService.determinarEstadoLuminaria(sensor).iconoMapa,
            sensor,
          );
        }
      });
    }

    //ubicacionLanzasHumedad
    if (this.ubicacionLanzasHumedad.length > 0) {
      this.ubicacionLanzasHumedad.forEach((sensor) => {
        if (sensor?.ubicacion !== undefined) {
          this.addMarker(sensor?.ubicacion, 'Lanza de Humedad', shsIconUrl);
        }
      });
    }

    //ubicacionSensoresHumedadSuelo
    if (this.ubicacionSensoresHumedadSuelo.length > 0) {
      this.ubicacionSensoresHumedadSuelo.forEach((sensor) => {
        if (sensor?.ubicacion?.coordenadas !== undefined) {
          this.addMarker(
            sensor?.ubicacion?.coordenadas,
            'Sensor de Humedad de Suelo',
            shsIconUrl,
          );
        }
      });
    }

    // pluviometros
    if (this.ubicacionPluviometros.length > 0) {
      this.ubicacionPluviometros.forEach((sensor) => {
        if (sensor?.ubicacion?.coordenadas !== undefined) {
          this.addMarker(
            sensor?.ubicacion?.coordenadas,
            'Pluviómetro',
            pluviometroIconUrl,
          );
        }
      });
    }

    // Estaciones Meteorologicas
    if (this.ubicacionEstacion.length > 0) {
      this.ubicacionEstacion.forEach((sensor) => {
        if (sensor?.ubicacion?.coordenadas !== undefined) {
          this.addMarker(
            sensor?.ubicacion?.coordenadas,
            'Estación Meteorológica',
            estacionIconUrl,
          );
        }
      });
    }

    // Freatimetros
    if (this.ubicacionFreatimetro.length > 0) {
      this.ubicacionFreatimetro.forEach((sensor) => {
        if (sensor?.ubicacion?.coordenadas !== undefined) {
          this.addMarker(
            sensor?.ubicacion?.coordenadas,
            'Freatímetro',
            freatimetroIconUrl,
          );
        }
      });
    }

    // Dispositivos de presión de agua
    if (this.ubicacionPresionAgua.length > 0) {
      this.ubicacionPresionAgua.forEach((dispositivo) => {
        if (dispositivo?.ubicacion !== undefined) {
          this.addMarker(
            dispositivo.ubicacion,
            dispositivo.nombre || 'Dispositivo de Presión',
            presionAguaIconUrl,
            undefined, // no es luminaria
            dispositivo, // pasar el dispositivo para el click
          );
        }
      });
    }
  }

  public calcularPromedioHumedad(reporte: any): number | null {
    if (!reporte || !reporte.humedad) return null;
    const valores = Object.values(reporte.humedad) as number[];
    if (valores.length === 0) return null;
    const suma = valores.reduce((acc: number, val: number) => acc + val, 0);
    return +(suma / valores.length).toFixed(2);
  }

  public calcularPromedioTemperatura(reporte: any): number | null {
    if (!reporte || !reporte.temperatura) return null;
    const valores = Object.values(reporte.temperatura) as number[];
    if (valores.length === 0) return null;
    const suma = valores.reduce((acc: number, val: number) => acc + val, 0);
    return +(suma / valores.length).toFixed(2);
  }

  private actualizarPivotSeleccionado() {
    // Si no hay ambiente definido, no hacer nada
    if (!this.ambiente) {
      this.pivotSeleccionadoId = undefined;
      console.log('No hay ambiente definido, pivot deseleccionado');
      return;
    }

    // Si el ambiente tiene círculo, seleccionar el pivot
    console.log(this.ambiente?.ubicacion?.circulo, 'circulo');
    console.log(this.ambiente, 'ambiente');
    if (this.ambiente?.ubicacion?.circulo) {
      if (this.pivots.length > 0) {
        // ✅ Usar el primer pivot disponible
        this.pivotSeleccionadoId = this.pivots[0]._id;
        console.log('Pivot seleccionado:', this.pivotSeleccionadoId);
      }
    } else {
      this.pivotSeleccionadoId = undefined;
      console.log('No hay círculo, pivot deseleccionado');
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.processUbicaciones();
    this.infoWindow?.close();
    this.dibujando = false;
    const otrasUbicaciones = this.otrasUbicaciones;

    if (changes.esCircle && !changes.esCircle.firstChange) {
      if (this.map?.googleMap) {
        this.initDrawingManager();
        this.drawingManager.setMap(this.map.googleMap);
      }
    }

    if (
      changes.ubicacionLanzasHumedad ||
      changes.ubicacionSensoresHumedadSuelo ||
      changes.ubicacionPluviometros ||
      changes.ubicacionEstacion ||
      changes.ubicacionFreatimetro ||
      changes.ubicacionLuminaria ||
      changes.ubicacionPresionAgua
    ) {
      this.updateMarkers();
    }

    if (otrasUbicaciones.length && !this.getUbicaciones?.length) {
      this.center = otrasUbicaciones[0].centro;
    }

    if (changes.cLinea) {
      this.cLinea = changes.cLinea.currentValue;
    }

    if (changes.cRelleno) {
      this.cRelleno = changes.cRelleno.currentValue;
    }
    this.actualizarPivotSeleccionado();
  }

  async ngOnInit(): Promise<void> {
    // Suscribirse a los cambios en los filtros activos
    this.luminariasService.filtrosActivos$.subscribe(() => {
      this.actualizarLuminariasFiltradas();
      this.updateMarkers();
    });
    await this.initMap();

    this.actualizarPivotSeleccionado();
  }
}
